import type { Graph, Product } from 'schema-dts'
import { NUMBER_OF_EVENTS } from '~/composables/product/constants'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'
import { KONFETTI_COMPANY } from '~~/constants/company'

const FALLBACK_IMAGE = `${KONFETTI_COMPANY.url}_nuxt3-static/promo-cards/giftbox.webp`

// One product with an offer per preset amount: it's a single giftcard where
// the buyer picks the value, not four products on the same URL.
const useGiftcardsSchema = (
  giftcards: number[],
  image?: string,
  countryDeliveryCosts?: Partial<Record<string, number>>
): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))
  const fullLocalizedUrl = useFullLocalizedUrl()
  const { t, locale } = useI18n()
  const eventCount = formattedNumberOfEvents(locale.value, NUMBER_OF_EVENTS)
  const url = fullLocalizedUrl('/giftcard/')

  const product: Product = {
    '@type': 'Product',
    '@id': `${url}#product`,
    name: t('giftcard.seo.name'),
    url,
    description: t('giftcard.seo.description', { eventCount }),
    category: 'Gift Card',
    image: image || FALLBACK_IMAGE,
    brand: organizationReference,
    offers: giftcards.map((giftcard) =>
      getGiftcardOffer({
        price: giftcard,
        url,
        sku: `KONFETTI-GIFTCARD-${giftcard}`,
        countryDeliveryCosts,
      })
    ),
  }

  schema.value = { '@context': 'https://schema.org', '@graph': [product] }

  return {
    schema,
    script,
  }
}

export default useGiftcardsSchema
